import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Spinner, Card } from 'react-bootstrap';
import { ReactComponent as EditIcon } from '../../../assets/images/Edit.svg';
import { ReactComponent as DeleteIcon } from '../../../assets/images/Delete.svg';
import AddNewButton from '../../../Common/AddNewButton';
import Select from 'react-select';
import { getPlayerById } from '../../../api/tournament/playerService';
import { listTeams } from '../../../api/tournament/teamService';
import { DeleteModal } from '../../../Common/DeleteModal';
import LogoUpload from '../../../components/LogoUpload/LogoUpload';
import { getPresignedUrl, PresignedUrlRequest, PresignedUrlResponse } from '../../../api/tournament/fixturesService';
import { bulkUploadPlayerStats, BulkUploadPlayerStatsRequest } from '../../../api/tournament/playerStatsService';
import '../../../Common/ManageTournamentsFixturesTable.css';
import ShimmerLoader from '../../../Common/ShimmerLoader';
import { getImageUrl } from '../../../utils/imageUrl';
import TournamentBulkUploadModal from '../../../components/tournament/TournamentBulkUploadModal';
import { getBulkUploadSuccessHandler } from './playerBulkUpload';

interface Player {
  _id: string;
  name?: string; // Optional because API might have it nested in playerInfo
  profilePic?: string | null;
  position?: string;
  ageCategory?: string; // Optional because API might have it at root level
  gender?: string;
  team?: { name: string; shortName: string; logo?: string };
  teamInfo?: { name: string; shortName: string; logo?: string };
  uniqueId?: number; // Optional because API might have it nested in playerInfo
  // Support for nested API response structure
  playerInfo?: {
    _id?: string;
    uniqueId?: number;
    name?: string;
    profilePic?: string | null;
  };
  // Player season info (for season context)
  playerSeasonInfo?: {
    _id?: string;
    player?: string;
    sports?: string;
    tournament?: string;
    tournamentSeason?: string;
    team?: {
      _id?: string;
      name?: string;
      shortName?: string;
      logo?: string;
    };
    teamSeason?: {
      _id?: string;
    };
    ageCategory?: string;
    position?: string;
    gender?: string;
    dob?: string;
    jerseyNum?: number;
    sportAttributes?: {
      preferredFoot?: string;
      position?: string;
    };
  };
  sports?: string;
  // Player stats properties
  goals?: number;
  assists?: number;
  passingAccuracy?: number;
  shotsOnTarget?: number;
  tackles?: number;
  playerStats?: any; // Support for nested playerStats
}

interface OptionType {
  value: string;
  label: string;
  format?: string; // Optional format property (e.g., 'player', 'Team')
  standingsType?: string; // Optional standingsType for fixtures
}

interface PlayersTableProps {
  players: Player[];
  search: string;
  onSearch: (value: string) => void;
  ageCategoryFilter: OptionType | null;
  onAgeCategoryFilterChange: (option: OptionType | null) => void;
  onAdd: (player?: any) => void;
  onEdit: (player: any) => void;
  onDelete: (playerId: string, sport: string) => void;
  teams: OptionType[];
  ageCategories: OptionType[];
  loading: boolean;
  page: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  tournamentId: string;
  isPlayerStats?: boolean;
  onOpenPlayerStatsDetail?: (playerId: string) => void;
  onRefresh?: () => void;
  tournamentSeasonId?: string; // Optional: tournamentSeason id for season context
  tournamentSports?: string; // Optional: tournament sports (e.g., "Football")
}

const genderOptions = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const positionOptions = [
  { value: 'goalkeeper', label: 'Goalkeeper' },
  { value: 'defender', label: 'Defender' },
  { value: 'midfielder', label: 'Midfielder' },
  { value: 'forward', label: 'Forward' },
];

const preferredFootOptions = [
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'both', label: 'Both' },
];

const playingHandOptions = [
  { value: 'left', label: 'Left' },
  { value: 'right', label: 'Right' },
  { value: 'both', label: 'Both' },
];

const preferredPositionOptions = [
  { value: 'front', label: 'Front' },
  { value: 'back', label: 'Back' },
  { value: 'both', label: 'Both' },
];

const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

const PlayersTable: React.FC<PlayersTableProps> = ({ players, search, onSearch, ageCategoryFilter, onAgeCategoryFilterChange, onAdd, onEdit, onDelete, teams, ageCategories, loading, page, pageSize, totalPages, totalCount, onPageChange, onPageSizeChange, tournamentId, isPlayerStats = false, onOpenPlayerStatsDetail, onRefresh, tournamentSeasonId, tournamentSports }) => {
  // Helper to get sport - use prop if provided, otherwise throw error
  const getSport = (): string => {
    if (tournamentSports) {
      return tournamentSports;
    }
    throw new Error('Tournament sport is required. Please ensure tournamentSports prop is passed to PlayersTable.');
  };
  const [showAddForm, setShowAddForm] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState(false);
  const [form, setForm] = useState<any>({
    name: '',
    position: null,
    ageCategory: null,
    team: null,
    gender: genderOptions[0],
    profilePic: '',
    dob: '',
    jerseyNum: '',
    preferredFoot: null,
    playingHand: null,
    preferredPosition: null,
  });
  // Store teamSeason info for selected team
  const [selectedTeamSeasonId, setSelectedTeamSeasonId] = useState<string | null>(null);
  // Store teams with teamSeason info
  const [teamsWithSeasonInfo, setTeamsWithSeasonInfo] = useState<any[]>([]);
  const [formTouched, setFormTouched] = useState<any>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [isFormModified, setIsFormModified] = useState(false);
  const [originalForm, setOriginalForm] = useState<any>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Team filtering state
  const [filteredTeams, setFilteredTeams] = useState<OptionType[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);

  // Debounced search input state
  const [inputValue, setInputValue] = useState(search);
  const inputRef = useRef<HTMLInputElement>(null);

  // Bulk upload state (only for player stats)
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadResponseData, setUploadResponseData] = useState<any>(null);
  const [showResponseData, setShowResponseData] = useState(false);
  const [showTournamentBulkUpload, setShowTournamentBulkUpload] = useState(false);

  // Load teams by age category
  const loadTeamsByAgeCategory = useCallback(async (ageCategoryId: string): Promise<OptionType[]> => {
    if (!ageCategoryId || !tournamentId) {
      console.warn('loadTeamsByAgeCategory: Missing ageCategoryId or tournamentId', { ageCategoryId, tournamentId });
      setFilteredTeams([]);
      setTeamsWithSeasonInfo([]);
      return [];
    }
    
    const ageCategoryObj = ageCategories.find(ac => ac.value === ageCategoryId);
    if (!ageCategoryObj) {
      console.error('Age category object not found for ID:', ageCategoryId, 'Available age categories:', ageCategories);
      setFilteredTeams([]);
      setTeamsWithSeasonInfo([]);
      return [];
    }
    
    // Use label (age category name) for API call
    // Fallback to value if label is not available
    const ageCategoryName = ageCategoryObj.label || ageCategoryObj.value;
    
    setTeamsLoading(true);
    setFormError(null); // Clear any previous errors
    try {
      const apiParams = {
        tournamentId: tournamentId,
        ageCategory: ageCategoryName, // Use the label (name) instead of ID
        pageSize: 1000, // Get all teams for this age category
        tournamentSeason: tournamentSeasonId, // Pass tournamentSeason to get teamSeason info
        sport: getSport()
      };
      
      const response = await listTeams(apiParams);
      
      // Handle different response structures
      // teamService already unwraps response.data, so response should be the data object
      const teamsData = response.paginatedResults || response.data?.paginatedResults || [];
      
      // If no teams found with age category filter, try without it to see if teams exist for this season
      if (teamsData.length === 0) {
        console.warn('No teams found for age category:', ageCategoryName, 'with tournamentSeason:', tournamentSeasonId);
        
        try {
          const allTeamsResponse = await listTeams({
            tournamentId: tournamentId,
            ageCategory: '', // No age category filter
            pageSize: 1000,
            tournamentSeason: tournamentSeasonId,
            sport: getSport()
          });
          
          const allTeams = allTeamsResponse.paginatedResults || allTeamsResponse.data?.paginatedResults || [];
          
          if (allTeams.length > 0) {
            // Filter teams by age category manually to see if there's a mismatch
            const teamsWithMatchingAgeCategory = allTeams.filter((team: any) => {
              const teamAgeCategories = team.teamSeasonInfo?.ageCategory || team.ageCategory || [];
              const ageCatArray = Array.isArray(teamAgeCategories) ? teamAgeCategories : [teamAgeCategories];
              return ageCatArray.some((cat: string) => 
                cat.toLowerCase().trim() === ageCategoryName.toLowerCase().trim()
              );
            });
            
            if (teamsWithMatchingAgeCategory.length > 0) {
              // Use manually filtered teams
              setTeamsWithSeasonInfo(teamsWithMatchingAgeCategory);
              // NEW API STRUCTURE: _id is teamSeason id, team._id is the actual team id
              const options = teamsWithMatchingAgeCategory.map((team: any) => {
                const teamData = team.team || team; // Use nested team object if available
                return { 
                  value: teamData._id || team._id, // Use team._id (actual team ID) first
                  label: teamData.name || team.name 
                };
              });
              setFilteredTeams(options);
              return options;
            }
          }
        } catch (fallbackError) {
          console.error('Error loading all teams:', fallbackError);
        }
      }
      
      // Store teams with teamSeason info
      setTeamsWithSeasonInfo(teamsData);
      
      // NEW API STRUCTURE: _id is teamSeason id, team._id is the actual team id
      const options = teamsData.map((team: any) => {
        const teamData = team.team || team; // Use nested team object if available
        return { 
          value: teamData._id || team._id, // Use team._id (actual team ID) first
          label: teamData.name || team.name 
        };
      });
      
      setFilteredTeams(options);
      
      if (options.length === 0) {
        console.warn('No teams found for age category:', ageCategoryName, 'with tournamentSeason:', tournamentSeasonId);
        setFormError(`No teams found for age category "${ageCategoryName}". Please ensure teams are added for this age category in the Teams tab.`);
      }
      
      // Return the options so they can be used immediately
      return options;
    } catch (error: any) {
      console.error('Error loading teams by age category:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setFilteredTeams([]);
      setTeamsWithSeasonInfo([]);
      // Show error message to user
      setFormError(`Failed to load teams: ${error.response?.data?.error || error.message || 'Unknown error'}`);
      return [];
    } finally {
      setTeamsLoading(false);
    }
  }, [tournamentId, ageCategories, tournamentSeasonId]);

  // Helper to get selected age category format
  const getSelectedAgeCategoryFormat = (): string | null => {
    if (!form.ageCategory?.value || !ageCategories.length) return null;
    // Find the full age category object to get format property
    // ageCategories can be either array of objects with _id/format or array of options with value/label/format
    const ageCategoryObj = ageCategories.find((ac: any) => 
      ac._id === form.ageCategory.value || ac.value === form.ageCategory.value
    );
    // Check format property (can be on OptionType or direct object)
    return (ageCategoryObj as any)?.format || null;
  };

  // Check if selected age category has 'player' format (case-insensitive)
  const isPlayerFormat = (): boolean => {
    const format = getSelectedAgeCategoryFormat();
    return format?.toLowerCase() === 'player';
  };

  // Load teams when age category changes (only if format is not 'player')
  useEffect(() => {
    if (form.ageCategory?.value && ageCategories.length > 0) {
      // Only load teams if age category format is NOT 'player'
      if (!isPlayerFormat()) {
        loadTeamsByAgeCategory(form.ageCategory.value);
      } else {
        setFilteredTeams([]);
        // Clear team selection when switching to player format
        setForm({ ...form, team: null });
      }
    } else {
      if (form.ageCategory?.value && ageCategories.length === 0) {
      }
      setFilteredTeams([]);
    }
  }, [form.ageCategory, loadTeamsByAgeCategory, ageCategories.length]);

  useEffect(() => {
    if (search === '' && inputValue !== '') {
      setInputValue('');
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          const len = inputRef.current.value.length;
          inputRef.current.setSelectionRange(len, len);
        }
      }, 0);
    }
  }, [search]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (inputValue.length === 0 || inputValue.length >= 2) {
        onSearch(inputValue);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [inputValue, onSearch]);

  useEffect(() => {
    if (!showAddForm) {
      setForm({
        name: '',
        position: null,
        ageCategory: null,
        team: null,
        // gender: genderOptions[0], // Gender field hidden for now
        profilePic: '',
        dob: '',
        jerseyNum: '',
        preferredFoot: null,
        playingHand: null,
        preferredPosition: null,
      });
      setFormTouched({});
      setFormError(null);
    }
  }, [showAddForm]);

  const validateForm = () => {
    // Team is only required if age category format is NOT 'player'
    const teamRequired = !isPlayerFormat();
    return {
      name: !form.name.trim() ? 'Name is required.' : '',
      position: '', // Position is optional
      ageCategory: !form.ageCategory ? 'Age Category is required.' : '',
      team: teamRequired && !form.team ? 'Team is required.' : '',
      gender: '', // Gender is hidden for now
      profilePic: '', // Profile Picture is optional
    };
  };
  const errors = validateForm();
  const isFormValid = Object.values(errors).every(x => !x);

  // Check if form has been modified
  const checkFormModified = () => {
    if (!originalForm) return false;
    return JSON.stringify(form) !== JSON.stringify(originalForm);
  };

  // Update form modified state whenever form changes
  React.useEffect(() => {
    setIsFormModified(checkFormModified());
  }, [form, originalForm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setFormTouched({ ...formTouched, [e.target.name]: true });
  };
  const handleSelectChange = (name: string, option: any) => {
    if (name === 'ageCategory') {
      // Clear team selection when age category changes
      // Also check if new age category has player format
      const newAgeCategoryFormat = (option as any)?.format || 
        (ageCategories.find((ac: any) => ac._id === option?.value || ac.value === option?.value) as any)?.format;
      const isPlayerFormatCategory = newAgeCategoryFormat?.toLowerCase() === 'player';
      
      setForm({ 
        ...form, 
        [name]: option, 
        team: isPlayerFormatCategory ? null : form.team // Clear team only if switching to player format
      });
      setFormTouched({ ...formTouched, [name]: true, team: true });
      setSelectedTeamSeasonId(null); // Clear teamSeason when age category changes
    } else if (name === 'team') {
      // When team is selected, find and store teamSeasonInfo._id if available
      // NEW API STRUCTURE: _id is teamSeason id, team._id is the actual team id
      if (option?.value) {
        // Find team by matching team._id (actual team ID) or _id (teamSeason ID) for backward compatibility
        const selectedTeam = teamsWithSeasonInfo.find((t: any) => {
          const teamData = t.team || t; // Use nested team object if available
          return teamData._id === option.value || t._id === option.value;
        });
        // _id is now teamSeason id
        const teamSeasonId = selectedTeam?._id || selectedTeam?.teamSeasonInfo?._id || null;
        setForm({ ...form, [name]: {
          teamId: option.value,
          teamName: option.label,
          teamSeasonId: teamSeasonId,
          selectedTeam: selectedTeam
        }});
        setSelectedTeamSeasonId(teamSeasonId);
      } else {
        setSelectedTeamSeasonId(null);
      }
      setForm({ ...form, [name]: option });
      setFormTouched({ ...formTouched, [name]: true });
    } else {
      setForm({ ...form, [name]: option });
      setFormTouched({ ...formTouched, [name]: true });
    }
  };
  const handleGenderChange = (option: any) => {
    setForm({ ...form, gender: option });
    setFormTouched({ ...formTouched, gender: true });
  };
  const handleProfilePicChange = async (logoUrl: string) => {
    setForm({ ...form, profilePic: logoUrl });
    setFormTouched({ ...formTouched, profilePic: true });
  };

  const scrollToCard = () => {
    if (cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleAdd = () => {
    setEditIndex(null);
    setViewMode(false);
    setSelectedTeamSeasonId(null);
    const initialForm = {
      name: '',
      position: null,
      ageCategory: null,
      team: null,
      gender: genderOptions[0],
      profilePic: '',
      dob: '',
      jerseyNum: '',
      preferredFoot: null,
      playingHand: null,
      preferredPosition: null,
    };
    setForm(initialForm);
    setOriginalForm(initialForm);
    setIsFormModified(false);
    setFormTouched({});
    setFormError(null);
    setShowAddForm(true);
    setTimeout(scrollToCard, 100); // Small delay to ensure card is rendered
  };
  const handleEdit = async (idx: number) => {
    // Ensure players is always an array
    const playersArray = Array.isArray(players) ? players : [];
    if (isPlayerStats && onOpenPlayerStatsDetail) {
      const p = playersArray[idx];
      onOpenPlayerStatsDetail(p._id);
      return;
    }
    setEditIndex(idx);
    setViewMode(false);
    const p = playersArray[idx];
    
    let formData;
    let teamId = ''; // Declare outside try block so it's accessible after await
    try {
      // Fetch detailed player data using the API
      // Pass tournamentSeasonId to get playerSeasonInfo in the response
      const playerData = await getPlayerById(p._id, tournamentSeasonId || undefined, getSport());
      const player = playerData.data || playerData; // Handle different response structures
      
      // Check if playerSeasonInfo exists (for season context)
      const playerSeasonInfo = player.playerSeasonInfo;
      
      // Extract preferredFoot from sportAttributes if available
      const preferredFootValue = player.sportAttributes?.preferredFoot || playerSeasonInfo?.sportAttributes?.preferredFoot || null;
      const preferredFootOption = preferredFootValue ? preferredFootOptions.find(opt => opt.value === preferredFootValue) : null;
      
      // Extract Badminton-specific fields from sportAttributes if available
      const playingHandValue = player.sportAttributes?.playingHand || playerSeasonInfo?.sportAttributes?.playingHand || null;
      const playingHandOption = playingHandValue ? playingHandOptions.find(opt => opt.value === playingHandValue) : null;
      
      const preferredPositionValue = player.sportAttributes?.preferredPosition || playerSeasonInfo?.sportAttributes?.preferredPosition || null;
      const preferredPositionOption = preferredPositionValue ? preferredPositionOptions.find(opt => opt.value === preferredPositionValue) : null;
      
      // Determine team information - prioritize playerSeasonInfo.team if available
      let teamName = '';
      let teamSeasonIdFromPlayer = null;
      if (playerSeasonInfo?.team) {
        // Use team from playerSeasonInfo (season context)
        teamName = playerSeasonInfo.team.name || '';
        teamId = playerSeasonInfo.team._id || '';
        // Also get teamSeason from playerSeasonInfo if available
        teamSeasonIdFromPlayer = playerSeasonInfo?.teamSeason || null;
      } else {
        // Fallback to player.team or player.teamInfo (non-season context)
        teamName = player.team?.name || player.teamInfo?.name || '';
        teamId = player.team?._id || player.teamInfo?._id || '';
      }
      
      // Find team in the teams list - try matching by team ID first, then by name
      // NEW API STRUCTURE: teams dropdown uses team._id (actual team ID) as value
      let selectedTeam = teams.find(opt => opt.value === teamId);
      if (!selectedTeam) {
        // Fallback: try matching by name
        selectedTeam = teams.find(opt => opt.label === teamName);
      }
      
      // If still not found, try to find in teamsWithSeasonInfo and create option
      if (!selectedTeam && teamsWithSeasonInfo.length > 0) {
        const teamFromList = teamsWithSeasonInfo.find((t: any) => {
          const teamData = t.team || t;
          return teamData._id === teamId || teamData.name === teamName;
        });
        if (teamFromList) {
          const teamData = teamFromList.team || teamFromList;
          selectedTeam = { value: teamData._id, label: teamData.name };
        }
      }
      
      // Get teamSeason info - prioritize from playerSeasonInfo, then from teamsWithSeasonInfo
      let teamSeasonId = teamSeasonIdFromPlayer;
      if (!teamSeasonId && selectedTeam) {
        // Find team by matching team._id (actual team ID) or _id (teamSeason ID) for backward compatibility
        const teamFromList = teamsWithSeasonInfo.find((t: any) => {
          const teamData = t.team || t; // Use nested team object if available
          return selectedTeam && (teamData._id === selectedTeam.value || t._id === selectedTeam.value);
        });
        // _id is now teamSeason id
        teamSeasonId = teamFromList?._id || teamFromList?.teamSeasonInfo?._id || null;
      }
      setSelectedTeamSeasonId(teamSeasonId);
      
      // Determine age category - check playerSeasonInfo first, then fallback
      const ageCategoryValue = playerSeasonInfo?.ageCategory || player.ageCategory || '';
      
      // Extract position - check sportAttributes first, then root level
      const positionValue = playerSeasonInfo?.sportAttributes?.position || playerSeasonInfo?.position || player.sportAttributes?.position || player.position || null;
      const positionOption = positionValue ? positionOptions.find(opt => opt.value === positionValue) : null;
      
      // Determine age category option
      const ageCategoryOption = ageCategories.find(opt => opt.label === ageCategoryValue) || null;
      
      // If age category is set, load teams for that age category first BEFORE setting formData
      // This ensures teams are available for matching
      if (ageCategoryOption?.value) {
        const loadedTeams = await loadTeamsByAgeCategory(ageCategoryOption.value);
        
        // After teams are loaded, match the team using the returned teams array
        if (teamId && loadedTeams && loadedTeams.length > 0) {
          const updatedSelectedTeam = loadedTeams.find(opt => opt.value === teamId);
          if (updatedSelectedTeam) {
            selectedTeam = updatedSelectedTeam;
          } else {
            // Fallback: try matching by name
            const teamByName = loadedTeams.find(opt => opt.label === teamName);
            if (teamByName) {
              selectedTeam = teamByName;
            } else {
              console.warn('Team not found in loaded teams:', {
                teamId,
                teamName,
                loadedTeams: loadedTeams.map(t => ({ value: t.value, label: t.label }))
              });
            }
          }
        }
      }
      
      // Now set formData with the correctly matched team
      formData = {
        name: player.name || player.playerInfo?.name || '',
        position: positionOption,
        ageCategory: ageCategoryOption,
        team: selectedTeam || null,
        gender: (playerSeasonInfo?.gender || player.gender) ? genderOptions.find(opt => opt.value.toLowerCase() === (playerSeasonInfo?.gender || player.gender).toLowerCase()) || genderOptions[0] : genderOptions[0],
        profilePic: player.profilePic || player.playerInfo?.profilePic || '',
        dob: playerSeasonInfo?.dob || player.dob || '',
        jerseyNum: (playerSeasonInfo?.jerseyNum || player.jerseyNum)?.toString() || '',
        preferredFoot: preferredFootOption,
        playingHand: playingHandOption,
        preferredPosition: preferredPositionOption,
      };
      
    } catch (error) {
      console.error('Error fetching player data:', error);
      // Fallback to using data from the list if API call fails
      formData = {
        name: p.name || p.playerInfo?.name || '',
      position: p.position ? positionOptions.find(opt => opt.value === p.position) : null,
      ageCategory: ageCategories.find(opt => opt.label === p.ageCategory) || null,
        team: teams.find(opt => opt.label === (p.team?.name || p.teamInfo?.name || '')) || null,
        gender: genderOptions[0],
        profilePic: p.profilePic || p.playerInfo?.profilePic || '',
        dob: '',
        jerseyNum: '',
        preferredFoot: null,
        playingHand: null,
        preferredPosition: null,
      };
    }
    
    setForm(formData);
    setOriginalForm(JSON.parse(JSON.stringify(formData))); // Deep copy
    setIsFormModified(false);
    setFormTouched({});
    setFormError(null);
    setShowAddForm(true);
    setTimeout(scrollToCard, 100); // Small delay to ensure card is rendered
  };

  const handleView = async (idx: number) => {
    // Ensure players is always an array
    const playersArray = Array.isArray(players) ? players : [];
    if (isPlayerStats && onOpenPlayerStatsDetail) {
      const p = playersArray[idx];
      onOpenPlayerStatsDetail(p._id);
      return;
    }
    setEditIndex(idx);
    setViewMode(true);
    const p = playersArray[idx];
    
    let formData;
    let teamId = ''; // Declare outside try block so it's accessible after await
    try {
      // Fetch detailed player data using the API
      // Pass tournamentSeasonId to get playerSeasonInfo in the response
      const playerData = await getPlayerById(p._id, tournamentSeasonId || undefined, getSport());
      const player = playerData.data || playerData; // Handle different response structures
      
      // Check if playerSeasonInfo exists (for season context)
      const playerSeasonInfo = player.playerSeasonInfo;
      
      // Extract preferredFoot from sportAttributes if available
      const preferredFootValue = player.sportAttributes?.preferredFoot || playerSeasonInfo?.sportAttributes?.preferredFoot || null;
      const preferredFootOption = preferredFootValue ? preferredFootOptions.find(opt => opt.value === preferredFootValue) : null;
      
      // Extract Badminton-specific fields from sportAttributes if available
      const playingHandValue = player.sportAttributes?.playingHand || playerSeasonInfo?.sportAttributes?.playingHand || null;
      const playingHandOption = playingHandValue ? playingHandOptions.find(opt => opt.value === playingHandValue) : null;
      
      const preferredPositionValue = player.sportAttributes?.preferredPosition || playerSeasonInfo?.sportAttributes?.preferredPosition || null;
      const preferredPositionOption = preferredPositionValue ? preferredPositionOptions.find(opt => opt.value === preferredPositionValue) : null;
      
      // Determine team information - prioritize playerSeasonInfo.team if available
      let teamName = '';
      let teamSeasonIdFromPlayer = null;
      if (playerSeasonInfo?.team) {
        // Use team from playerSeasonInfo (season context)
        teamName = playerSeasonInfo.team.name || '';
        teamId = playerSeasonInfo.team._id || '';
        // Also get teamSeason from playerSeasonInfo if available
        teamSeasonIdFromPlayer = playerSeasonInfo?.teamSeason || null;
      } else {
        // Fallback to player.team or player.teamInfo (non-season context)
        teamName = player.team?.name || player.teamInfo?.name || '';
        teamId = player.team?._id || player.teamInfo?._id || '';
      }
      
      // Find team in the teams list - try matching by team ID first, then by name
      // NEW API STRUCTURE: teams dropdown uses team._id (actual team ID) as value
      let selectedTeam = teams.find(opt => opt.value === teamId);
      if (!selectedTeam) {
        // Fallback: try matching by name
        selectedTeam = teams.find(opt => opt.label === teamName);
      }
      
      // If still not found, try to find in teamsWithSeasonInfo and create option
      if (!selectedTeam && teamsWithSeasonInfo.length > 0) {
        const teamFromList = teamsWithSeasonInfo.find((t: any) => {
          const teamData = t.team || t;
          return teamData._id === teamId || teamData.name === teamName;
        });
        if (teamFromList) {
          const teamData = teamFromList.team || teamFromList;
          selectedTeam = { value: teamData._id, label: teamData.name };
        }
      }
      
      // Get teamSeason info - prioritize from playerSeasonInfo, then from teamsWithSeasonInfo
      let teamSeasonId = teamSeasonIdFromPlayer;
      if (!teamSeasonId && selectedTeam) {
        // Find team by matching team._id (actual team ID) or _id (teamSeason ID) for backward compatibility
        const teamFromList = teamsWithSeasonInfo.find((t: any) => {
          const teamData = t.team || t; // Use nested team object if available
          return selectedTeam && (teamData._id === selectedTeam.value || t._id === selectedTeam.value);
        });
        // _id is now teamSeason id
        teamSeasonId = teamFromList?._id || teamFromList?.teamSeasonInfo?._id || null;
      }
      setSelectedTeamSeasonId(teamSeasonId);
      
      // Determine age category - check playerSeasonInfo first, then fallback
      const ageCategoryValue = playerSeasonInfo?.ageCategory || player.ageCategory || '';
      
      // Extract position - check sportAttributes first, then root level
      const positionValue = playerSeasonInfo?.sportAttributes?.position || playerSeasonInfo?.position || player.sportAttributes?.position || player.position || null;
      const positionOption = positionValue ? positionOptions.find(opt => opt.value === positionValue) : null;
      
      // Determine age category option
      const ageCategoryOption = ageCategories.find(opt => opt.label === ageCategoryValue) || null;
      
      // If age category is set, load teams for that age category first BEFORE setting formData
      // This ensures teams are available for matching
      if (ageCategoryOption?.value) {
        const loadedTeams = await loadTeamsByAgeCategory(ageCategoryOption.value);
        
        // After teams are loaded, match the team using the returned teams array
        if (teamId && loadedTeams && loadedTeams.length > 0) {
          const updatedSelectedTeam = loadedTeams.find(opt => opt.value === teamId);
          if (updatedSelectedTeam) {
            selectedTeam = updatedSelectedTeam;
          } else {
            // Fallback: try matching by name
            const teamByName = loadedTeams.find(opt => opt.label === teamName);
            if (teamByName) {
              selectedTeam = teamByName;
            } else {
              console.warn('Team not found in loaded teams:', {
                teamId,
                teamName,
                loadedTeams: loadedTeams.map(t => ({ value: t.value, label: t.label }))
              });
            }
          }
        }
      }
      
      // Now set formData with the correctly matched team
      formData = {
        name: player.name || player.playerInfo?.name || '',
        position: positionOption,
        ageCategory: ageCategoryOption,
        team: selectedTeam || null,
        gender: (playerSeasonInfo?.gender || player.gender) ? genderOptions.find(opt => opt.value.toLowerCase() === (playerSeasonInfo?.gender || player.gender).toLowerCase()) || genderOptions[0] : genderOptions[0],
        profilePic: player.profilePic || player.playerInfo?.profilePic || '',
        dob: playerSeasonInfo?.dob || player.dob || '',
        jerseyNum: (playerSeasonInfo?.jerseyNum || player.jerseyNum)?.toString() || '',
        preferredFoot: preferredFootOption,
        playingHand: playingHandOption,
        preferredPosition: preferredPositionOption,
      };
      
    } catch (error) {
      console.error('Error fetching player data:', error);
      // Fallback to using data from the list if API call fails
      formData = {
        name: p.name || p.playerInfo?.name || '',
      position: p.position ? positionOptions.find(opt => opt.value === p.position) : null,
      ageCategory: ageCategories.find(opt => opt.label === p.ageCategory) || null,
        team: teams.find(opt => opt.label === (p.team?.name || p.teamInfo?.name || '')) || null,
        gender: genderOptions[0],
        profilePic: p.profilePic || p.playerInfo?.profilePic || '',
        dob: '',
        jerseyNum: '',
        preferredFoot: null,
        playingHand: null,
        preferredPosition: null,
      };
    }
    
    setForm(formData);
    setOriginalForm(JSON.parse(JSON.stringify(formData))); // Deep copy
    setIsFormModified(false);
    setFormTouched({});
    setFormError(null);
    setShowAddForm(true);
    setTimeout(scrollToCard, 100); // Small delay to ensure card is rendered
  };

  const handleClose = () => setShowAddForm(false);

  const handleSave = async () => {
    setFormTouched({
      name: true,
      position: true,
      ageCategory: true,
      team: true,
    });
    if (!isFormValid) return;
    setFormError(null);
    setSaving(true);
    try {
      // Capitalize gender value (Male, Female, Other)
      const genderValue = form.gender?.value || 'male';
      const capitalizedGender = genderValue.charAt(0).toUpperCase() + genderValue.slice(1).toLowerCase();
      
      const payload: any = {
        // Player basic fields
        name: form.name,
        profilePic: form.profilePic || '',
        dob: form.dob || '',
        gender: capitalizedGender, // Capitalized: "Male", "Female", "Other"
        tournament: tournamentId,
        // Seasonal fields
        sports: tournamentSports,
        ageCategory: form.ageCategory.label, // Use the label (age category name like "U5")
        jerseyNum: form.jerseyNum ? parseInt(form.jerseyNum) : undefined,
      };
      
      // Only include team fields if age category format is NOT 'player'
      if (!isPlayerFormat() && form.team?.value) {
        payload.team = form.team.value; // Use the team ID
        
        // If tournamentSeasonId is provided (season context), also include it
        if (tournamentSeasonId) {
          payload.tournamentSeason = tournamentSeasonId;
        }
        
        // If teamSeason is available (from selectedTeamSeasonId), include it
        // This is required for season context - the API needs teamSeasonInfo._id, not just team._id
        if (selectedTeamSeasonId) {
          payload.teamSeason = selectedTeamSeasonId;
        } else if (tournamentSeasonId && form.team?.value) {
          // Fallback: try to find teamSeason from teamsWithSeasonInfo if not already set
          // NEW API STRUCTURE: _id is teamSeason id, team._id is the actual team id
          // Find team by matching team._id (actual team ID) or _id (teamSeason ID) for backward compatibility
          const selectedTeam = teamsWithSeasonInfo.find((t: any) => {
            const teamData = t.team || t; // Use nested team object if available
            return teamData._id === form.team.value || t._id === form.team.value;
          });
          // _id is now teamSeason id
          const teamSeasonId = selectedTeam?._id || selectedTeam?.teamSeasonInfo?._id;
          if (teamSeasonId) {
            payload.teamSeason = teamSeasonId;
          } else {
            console.warn('No teamSeason found for team:', form.team.value, 'Available teams:', teamsWithSeasonInfo.map((t: any) => ({ id: t._id, teamId: t.team?._id, teamSeasonId: t._id })));
          }
        }
      } else if (tournamentSeasonId) {
        // If player format, still include tournamentSeason if available
        payload.tournamentSeason = tournamentSeasonId;
      }
      
      // Build sportAttributes based on sports type
      const sportAttributes: any = {};
      const currentSport = getSport().toLowerCase();
      
      // Football-specific attributes
      if (form.preferredFoot?.value) {
        sportAttributes.preferredFoot = form.preferredFoot.value;
      }
      if (form.position?.value && currentSport === 'football') {
        sportAttributes.position = form.position.value;
      }
      
      // Badminton-specific attributes
      if (form.playingHand?.value && currentSport === 'badminton') {
        sportAttributes.playingHand = form.playingHand.value;
      }
      if (form.preferredPosition?.value && currentSport === 'badminton') {
        sportAttributes.preferredPosition = form.preferredPosition.value;
      }
      
      // For add: position goes in sportAttributes
      // For edit: position goes at root level AND in sportAttributes
      if (editIndex === null) {
        // Adding new player - position only in sportAttributes
        if (Object.keys(sportAttributes).length > 0) {
          payload.sportAttributes = sportAttributes;
        }
      } else {
        // Editing existing player - position at root level AND in sportAttributes
        if (form.position?.value) {
          payload.position = form.position.value;
        }
        if (Object.keys(sportAttributes).length > 0) {
          payload.sportAttributes = sportAttributes;
        }
      }
      
      
      if (editIndex === null) {
        await onAdd(payload);
      } else {
        const playersArray = Array.isArray(players) ? players : [];
        await onEdit({ ...payload, _id: playersArray[editIndex]._id });
      }
      setShowAddForm(false);
    } catch (error: any) {
      console.error('Error saving player:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      // Extract detailed error message from API response
      let errorMessage = 'Failed to save player. Please try again.';
      
      if (error.response) {
        // API responded with error status
        const responseData = error.response.data;
        
        if (responseData?.message) {
          // Use the specific error message from API
          errorMessage = responseData.message;
        } else if (responseData?.error) {
          // Alternative error field
          errorMessage = responseData.error;
        } else if (typeof responseData === 'string') {
          // Plain text error response
          errorMessage = responseData;
        } else if (responseData?.errors && Array.isArray(responseData.errors)) {
          // Multiple validation errors
          errorMessage = responseData.errors.join(', ');
        } else if (responseData?.details) {
          // Error with details
          errorMessage = responseData.details;
        }
      } else if (error.request) {
        // Request was made but no response received
        errorMessage = 'No response from server. Please check your connection and try again.';
      } else if (error.message) {
        // Other error
        errorMessage = error.message;
      }
      
      setFormError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (idx: number) => {
    setDeleteIndex(idx);
    setShowDeleteModal(true);
  };
  const confirmDelete = async () => {
    if (deleteIndex !== null) {
      try {
        const playersArray = Array.isArray(players) ? players : [];
        const playerRow = playersArray[deleteIndex];
        const sport = playerRow?.sports || playerRow?.playerSeasonInfo?.sports || '';
        await onDelete(playerRow._id, sport);
        setShowDeleteModal(false);
        setDeleteIndex(null);
      } catch {
        setFormError('Failed to delete player.');
      }
    }
  };
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteIndex(null);
  };

  // Bulk upload handlers (only for player stats)
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!validExtensions.includes(fileExtension)) {
        setUploadError('Please select a valid Excel file (.xlsx or .xls)');
        return;
      }
      
      setUploadFile(file);
      setUploadError(null);
    }
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      const validExtensions = ['.xlsx', '.xls'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      
      if (!validExtensions.includes(fileExtension)) {
        setUploadError('Please select a valid Excel file (.xlsx or .xls)');
        return;
      }
      
      setUploadFile(file);
      setUploadError(null);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleBulkUpload = async () => {
    if (!uploadFile || !tournamentId) return;

    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    try {
      // Step 1: Get presigned URL
      setUploadProgress(25);
      const presignedUrlRequest: PresignedUrlRequest = {
        fileName: uploadFile.name,
        contentType: uploadFile.type,
        context: 'spreadsheets'
      };

      const presignedResponse: PresignedUrlResponse = await getPresignedUrl(presignedUrlRequest);
      console.log('Presigned URL response:', presignedResponse);
      setUploadProgress(50);

      // Extract fileKey from response (API might return 'key' instead of 'fileKey')
      const actualFileKey = presignedResponse.fileKey || presignedResponse.key;
      console.log('Extracted fileKey:', actualFileKey);
      
      if (!actualFileKey) {
        throw new Error('No fileKey received from presigned URL response');
      }

      // Step 2: Upload file to S3
      console.log('Uploading file to S3:', {
        url: presignedResponse.presignedUrl,
        fileKey: actualFileKey,
        fileName: uploadFile.name,
        fileSize: uploadFile.size,
        fileType: uploadFile.type
      });
      
      const uploadResponse = await fetch(presignedResponse.presignedUrl, {
        method: 'PUT',
        body: uploadFile,
        headers: {
          'Content-Type': uploadFile.type,
        },
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('S3 upload failed:', { status: uploadResponse.status, errorText });
        throw new Error(`Failed to upload file to S3: ${uploadResponse.status} - ${errorText}`);
      }
      console.log('S3 upload successful');
      setUploadProgress(75);

      // Step 3: Process bulk upload
      const bulkUploadRequest: BulkUploadPlayerStatsRequest = {
        fileKey: actualFileKey
      };
      
      // Get sport for API call
      const sport = getSport();
      
      console.log('Calling bulk upload API with:', {
        tournamentId,
        tournamentSeasonId,
        sport,
        bulkUploadRequest,
        actualFileKey
      });
      
      const response = await bulkUploadPlayerStats(tournamentId, bulkUploadRequest, tournamentSeasonId, sport);
      setUploadProgress(100);

      // Debug logging
      console.log('Bulk upload response:', response);

      // Check if response has data.success field
      // Response structure: { statusCode: 200, status: "success", message: "...", data: { success: true/false, errors: [], unmatchedPlayers: [], unmatchedMatches: [], unmatchedTeams: [] } }
      const responseData = response?.data || response;
      const isSuccess = responseData?.success === true;

      if (isSuccess) {
        // Success - close modal and refresh
        setUploadSuccess(true);
        setTimeout(() => {
          handleBulkUploadClose();
          if (onRefresh) {
            onRefresh(); // Refresh the player stats list
          }
        }, 2000);
      } else {
        // Has errors or unmatched data - show in table format
        setUploadResponseData(response);
        setShowResponseData(true);
        setUploadSuccess(false);
        // Don't close modal or refresh player stats - let user review the response
      }

    } catch (error: any) {
      console.error('Bulk upload error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      
      // Extract detailed error message from API response
      let errorMessage = 'Upload failed. Please try again.';
      
      if (error.response) {
        // API responded with error status
        const responseData = error.response.data;
        
        if (responseData) {
          // Try different possible error message fields
          errorMessage = responseData.message || 
                        responseData.error || 
                        responseData.title || 
                        responseData.msg ||
                        (typeof responseData === 'string' ? responseData : 'Upload failed');
        }
      } else if (error.message) {
        // Other error
        errorMessage = error.message;
      }
      
      setUploadError(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleBulkUploadClose = () => {
    setShowBulkUploadModal(false);
    setUploadFile(null);
    setUploadError(null);
    setUploadProgress(0);
    setUploadSuccess(false);
    setUploadResponseData(null);
    setShowResponseData(false);
  };

  const handleReupload = () => {
    setUploadFile(null);
    setUploadError(null);
    setUploadProgress(0);
    setUploadSuccess(false);
    setUploadResponseData(null);
    setShowResponseData(false);
  };

  const handleAcknowledgeResponse = () => {
    handleBulkUploadClose();
    if (onRefresh) {
      onRefresh(); // Refresh even if there were errors/warnings
    }
  };

  // Filter players by age category
  // Ensure players is always an array to prevent undefined errors
  const playersArray = Array.isArray(players) ? players : [];
  const filteredPlayers = !ageCategoryFilter
    ? playersArray
    : playersArray.filter(player => player.ageCategory === ageCategoryFilter.label);

  // Extract stats from player data, returning default values (0) when stats don't exist
  const extractStats = (p: any) => {
    // Support different response shapes: direct fields or nested under playerStats/stats/playerStatistics
    const nested = Array.isArray(p.playerStats) ? p.playerStats[0] : (p.playerStats || p.stats || (Array.isArray(p.playerStatistics) ? p.playerStatistics[0] : p.playerStatistics));
    const src = nested || p;
    const sportLower = getSport().toLowerCase();
    const isRacketSport = sportLower === 'tennis' || sportLower === 'badminton' || sportLower === 'table tennis' || sportLower === 'tt';
    
    if (isRacketSport) {
      return {
        // Map from 'total' prefixed fields with fallback to non-prefixed for backward compatibility
        aces: src?.totalAces ?? src?.aces ?? 0,
        serviceFaults: src?.totalServiceFaults ?? src?.serviceFaults ?? 0,
        winners: src?.totalWinners ?? src?.winners ?? 0,
        unforcedErrors: src?.totalUnforcedErrors ?? src?.unforcedErrors ?? 0,
        setsWon: src?.totalSetsWon ?? src?.setsWon ?? 0,
      } as any;
    } else {
      return {
        goals: src?.goals ?? 0,
        assists: src?.assists ?? 0,
        passingAccuracy: src?.passingAccuracy ?? src?.passPercentage ?? src?.passAccuracy ?? 0,
        shotsOnTarget: src?.shotsOnTarget ?? src?.shots ?? 0,
        tackles: src?.tackles ?? 0,
      } as any;
    }
  };
  // Display all players regardless of stats
  const visiblePlayers = filteredPlayers;

  return (
    <div>
      {/* Header with Filters - aligned like Fixtures */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex align-items-center gap-3">
          <h4 className="mb-0">{isPlayerStats ? 'Player Stats' : 'Players'}</h4>
          <div style={{ minWidth: 250, position: 'relative' }}>
            <Form.Control
              ref={inputRef}
                type="text"
                placeholder="Search players..."
                value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
                  style={{
                minHeight: '38px',
                borderRadius: '6px',
                fontSize: '14px',
                border: '1px solid #ced4da',
                paddingRight: inputValue ? '30px' : '12px'
              }}
            />
            {inputValue && (
              <button
                type="button"
                onClick={() => setInputValue('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#6c757d',
                  cursor: 'pointer',
                  fontSize: '16px',
                  padding: '0',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                title="Clear search"
              >
                ×
              </button>
            )}
            </div>
          <div style={{ minWidth: 150 }}>
              <Select
                classNamePrefix="react-select"
                value={ageCategoryFilter}
                onChange={onAgeCategoryFilterChange}
                options={ageCategories}
                placeholder="Age Category"
                isClearable
              styles={{ 
                control: base => ({ 
                  ...base, 
                  minHeight: '38px', 
                  borderRadius: '6px',
                  fontSize: '14px'
                })
              }}
            />
          </div>
        </div>
        <div className="d-flex gap-2">
          {tournamentSeasonId && (
            <Button 
              variant="outline-primary" 
              onClick={() => setShowTournamentBulkUpload(true)}
              style={{ borderRadius: '6px' }}
            >
              <i className="ri-upload-cloud-2-line me-1"></i>
              Bulk Upload
            </Button>
          )}
          {!isPlayerStats && (
            <Button 
              variant="primary" 
              onClick={handleAdd}
              style={{ borderRadius: '6px' }}
            >
              Add New +
            </Button>
          )}
        </div>
                      </div>
      
      {/* Add/Edit Form */}
      {showAddForm && (
        <Card ref={cardRef} className="mb-4">
          <Card.Header>
            <h5 className="mb-0">
              {editIndex === null ? 'Add Player' : viewMode ? 'View Player' : 'Edit Player'}
            </h5>
          </Card.Header>
          <Card.Body>
            {formError && <div className="alert alert-danger">{formError}</div>}
            <Form>
              {/* Required Fields Section */}
              <div className="row">
                <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Name<span style={{ color: 'red' }}>*</span></Form.Label>
                <Form.Control name="name" value={form.name} onChange={handleInputChange} isInvalid={!!errors.name && formTouched.name} onBlur={() => setFormTouched({ ...formTouched, name: true })} readOnly={viewMode} />
                {(errors.name && formTouched.name) && <div className="text-danger">{errors.name}</div>}
              </Form.Group>
                </div>
                <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Age Category<span style={{ color: 'red' }}>*</span></Form.Label>
                <Select
                  classNamePrefix="react-select"
                  name="ageCategory"
                  value={form.ageCategory}
                  onChange={option => handleSelectChange('ageCategory', option)}
                  options={ageCategories}
                  placeholder="Select Age Category"
                  isClearable={false}
                  isDisabled={viewMode}
                  menuPortalTarget={document.body}
                  styles={{ 
                    control: base => ({ ...base, minHeight: '38px' }),
                    menuPortal: base => ({ ...base, zIndex: 10000 })
                  }}
                />
                {(errors.ageCategory && formTouched.ageCategory) && <div className="text-danger">{errors.ageCategory}</div>}
              </Form.Group>
                </div>
              </div>
              <div className="row">
                {/* Only show team field if age category format is NOT 'player' */}
                {!isPlayerFormat() && (
                <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Team<span style={{ color: 'red' }}>*</span></Form.Label>
                <Select
                  classNamePrefix="react-select"
                  name="team"
                  value={form.team}
                  onChange={option => handleSelectChange('team', option)}
                  options={filteredTeams}
                  placeholder={form.ageCategory ? "Select Team" : "Select Age Category first"}
                  isClearable={false}
                  isLoading={teamsLoading}
                  isDisabled={viewMode || !form.ageCategory || teamsLoading}
                  menuPortalTarget={document.body}
                  styles={{ 
                    control: base => ({ ...base, minHeight: '38px' }),
                    menuPortal: base => ({ ...base, zIndex: 10000 })
                  }}
                />
                {(errors.team && formTouched.team) && <div className="text-danger">{errors.team}</div>}
              </Form.Group>
                </div>
                )}
              </div>
              
              {/* Optional Fields Section */}
              <div className="row">
                <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Date of Birth</Form.Label>
                <Form.Control 
                  name="dob" 
                  type="date"
                  value={form.dob} 
                  onChange={handleInputChange} 
                  onBlur={() => setFormTouched({ ...formTouched, dob: true })} 
                  readOnly={viewMode} 
                />
              </Form.Group>
                </div>
                <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Gender</Form.Label>
                <Select
                  classNamePrefix="react-select"
                  name="gender"
                  value={form.gender}
                  onChange={handleGenderChange}
                  options={genderOptions}
                  placeholder="Select Gender"
                  isClearable={false}
                  isDisabled={viewMode}
                  menuPortalTarget={document.body}
                  styles={{ 
                    control: base => ({ ...base, minHeight: '38px' }),
                    menuPortal: base => ({ ...base, zIndex: 10000 })
                  }}
                />
              </Form.Group>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Jersey Number</Form.Label>
                <Form.Control 
                  name="jerseyNum" 
                  type="number"
                  value={form.jerseyNum} 
                  onChange={handleInputChange} 
                  onBlur={() => setFormTouched({ ...formTouched, jerseyNum: true })} 
                  readOnly={viewMode}
                  placeholder="Enter jersey number"
                />
              </Form.Group>
                </div>
                {getSport().toLowerCase() === 'football' && (
                <div className="col-md-6">
              <Form.Group className="mb-3">
                    <Form.Label className="fw-medium">
                      Position
                    </Form.Label>
                    <Select
                      classNamePrefix="react-select"
                      name="position"
                      value={form.position}
                      onChange={option => handleSelectChange('position', option)}
                      options={positionOptions}
                      placeholder="Select Position"
                      isClearable={true}
                      isDisabled={viewMode}
                      menuPortalTarget={document.body}
                      styles={{ 
                        control: base => ({ ...base, minHeight: '38px' }),
                        menuPortal: base => ({ ...base, zIndex: 10000 })
                      }}
                    />
                    {errors.position && formTouched.position && <div className="text-danger">{errors.position}</div>}
              </Form.Group>
                </div>
                )}
              </div>
              {getSport().toLowerCase() === 'football' && (
              <div className="row">
                <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Preferred Foot</Form.Label>
                <Select
                  classNamePrefix="react-select"
                  name="preferredFoot"
                  value={form.preferredFoot}
                  onChange={option => handleSelectChange('preferredFoot', option)}
                  options={preferredFootOptions}
                  placeholder="Select Preferred Foot"
                  isClearable={true}
                  isDisabled={viewMode}
                  menuPortalTarget={document.body}
                  styles={{ 
                    control: base => ({ ...base, minHeight: '38px' }),
                    menuPortal: base => ({ ...base, zIndex: 10000 })
                  }}
                />
              </Form.Group>
                </div>
              </div>
              )}
              {getSport().toLowerCase() === 'badminton' && (
              <div className="row">
                <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Playing Hand</Form.Label>
                <Select
                  classNamePrefix="react-select"
                  name="playingHand"
                  value={form.playingHand}
                  onChange={option => handleSelectChange('playingHand', option)}
                  options={playingHandOptions}
                  placeholder="Select Playing Hand"
                  isClearable={true}
                  isDisabled={viewMode}
                  menuPortalTarget={document.body}
                  styles={{ 
                    control: base => ({ ...base, minHeight: '38px' }),
                    menuPortal: base => ({ ...base, zIndex: 10000 })
                  }}
                />
              </Form.Group>
                </div>
                <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>Preferred Position</Form.Label>
                <Select
                  classNamePrefix="react-select"
                  name="preferredPosition"
                  value={form.preferredPosition}
                  onChange={option => handleSelectChange('preferredPosition', option)}
                  options={preferredPositionOptions}
                  placeholder="Select Preferred Position"
                  isClearable={true}
                  isDisabled={viewMode}
                  menuPortalTarget={document.body}
                  styles={{ 
                    control: base => ({ ...base, minHeight: '38px' }),
                    menuPortal: base => ({ ...base, zIndex: 10000 })
                  }}
                />
              </Form.Group>
                </div>
              </div>
              )}
              <div className="row">
                <div className="col-md-6">
                  <LogoUpload
                    currentLogo={form.profilePic}
                    onLogoChange={handleProfilePicChange}
                    isEditMode={!viewMode}
                    context="players/profile"
                    maxSizeMB={1}
                    maxWidthOrHeight={800}
                    size={80}
                    title="Profile Picture"
                    label="Profile Picture"
                  />
                </div>
              </div>
            </Form>
          </Card.Body>
          <Card.Footer>
            <div className="d-flex justify-content-end gap-2">
              {viewMode ? (
                <>
                  <Button variant="secondary" onClick={handleClose}>Close</Button>
                  <Button variant="primary" onClick={() => setViewMode(false)}>Edit</Button>
                </>
              ) : (
                <>
                  <Button variant="secondary" onClick={handleClose}>Cancel</Button>
                  <Button variant="primary" onClick={handleSave} disabled={!isFormValid || saving || !isFormModified}>{saving ? 'Saving...' : 'Save'}</Button>
                </>
              )}
            </div>
          </Card.Footer>
        </Card>
      )}
      
      <div className="manage-tournaments-table-container">
        <Table className={`mb-0 manage-tournaments-table players-table ${isPlayerStats ? 'player-stats-table' : ''}`}>
            <thead>
              <tr>
                <th style={{ fontSize: '14px', fontWeight: '600', width: '80px' }}>ID</th>
                <th style={{ fontSize: '14px', fontWeight: '600', width: '60px' }}>Pic</th>
                <th style={{ fontSize: '14px', fontWeight: '600' }}>Player</th>
                <th style={{ fontSize: '14px', fontWeight: '600' }}>Team</th>
                <th style={{ fontSize: '14px', fontWeight: '600' }}>Age</th>
                {isPlayerStats && (
                  (() => {
                    const sportLower = getSport().toLowerCase();
                    return sportLower === 'tennis' || sportLower === 'badminton' || sportLower === 'table tennis' || sportLower === 'tt';
                  })() ? (
                    <>
                      <th style={{ fontSize: '14px', fontWeight: '600' }}>Aces</th>
                      <th style={{ fontSize: '14px', fontWeight: '600' }}>Service Faults</th>
                      <th style={{ fontSize: '14px', fontWeight: '600' }}>Winners</th>
                      <th style={{ fontSize: '14px', fontWeight: '600' }}>Unforced Errors</th>
                      <th style={{ fontSize: '14px', fontWeight: '600' }}>Sets Won</th>
                    </>
                  ) : (
                    <>
                      <th style={{ fontSize: '14px', fontWeight: '600' }}>Goals</th>
                      <th style={{ fontSize: '14px', fontWeight: '600' }}>Assists</th>
                      <th style={{ fontSize: '14px', fontWeight: '600' }}>Pass%</th>
                      <th style={{ fontSize: '14px', fontWeight: '600' }}>Shots</th>
                      <th style={{ fontSize: '14px', fontWeight: '600' }}>Tackles</th>
                    </>
                  )
                )}
                <th style={{ fontSize: '14px', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={isPlayerStats ? 11 : 6}><ShimmerLoader type="table" rows={5} /></td>
                </tr>
              ) : visiblePlayers.length === 0 ? (
                <tr>
                  <td colSpan={isPlayerStats ? 11 : 6}>
                    <div className="text-center py-5">
                      <div className="empty-state">
                        <div className="empty-state-icon">
                          <i className="ri-user-line" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                        </div>
                        <div className="empty-state-title">{isPlayerStats ? 'No Player Stats Available' : 'No Players Found'}</div>
                        <div className="empty-state-description">
                          {isPlayerStats ? 'There are no player statistics available for the selected filters.' : 'There are no players available for this tournament.'}
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                visiblePlayers.map((player, idx) => (
                  <tr key={player._id}>
                    <td>
                      <span style={{ 
                        fontWeight: '600', 
                        fontSize: '14px',
                        color: '#6c757d'
                      }}>
                        #{player.uniqueId || player.playerInfo?.uniqueId || player._id}
                      </span>
                    </td>
                    <td>
                      <div className="player-profile-container">
                        {(player.profilePic || player.playerInfo?.profilePic) ? (
                          <img 
                            src={getImageUrl(player.profilePic || player.playerInfo?.profilePic || '')}
                            alt="Profile" 
                            className="player-profile-image"
                            onError={(e) => {
                              const target = e.currentTarget;
                              target.style.display = 'none';
                              const placeholder = target.parentElement?.querySelector('.player-profile-placeholder') as HTMLElement;
                              if (placeholder) {
                                placeholder.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        <div className={`player-profile-placeholder ${!(player.profilePic || player.playerInfo?.profilePic) ? 'visible' : ''}`}>
                          <i className="ri-user-line"></i>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span style={{ 
                        fontWeight: '500', 
                        fontSize: '14px',
                        color: '#212529'
                      }} title={player.name || player.playerInfo?.name || 'Unknown'}>
                        {(() => {
                          const playerName = player.name || player.playerInfo?.name || 'Unknown';
                          return playerName.length > 15 ? playerName.substring(0, 15) + '...' : playerName;
                        })()}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        {player.team?.logo || player.teamInfo?.logo ? (
                          <img 
                            src={player.team?.logo || player.teamInfo?.logo} 
                            alt="Team Logo" 
                            style={{ 
                              width: '24px', 
                              height: '24px', 
                              marginRight: '8px', 
                              borderRadius: '4px',
                              objectFit: 'cover',
                              border: '1px solid #e9ecef'
                            }}
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : null}
                        <span style={{ fontWeight: '500', fontSize: '14px' }} title={player.team?.shortName || player.team?.name || player.teamInfo?.shortName || player.teamInfo?.name || '-'}>
                          {player.team?.shortName || player.team?.name || player.teamInfo?.shortName || player.teamInfo?.name || '-'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span style={{ 
                        fontWeight: '500', 
                        fontSize: '14px',
                        color: '#495057'
                      }} title={player.ageCategory || 'Unknown'}>
                        {(() => {
                          const ageCat = player.ageCategory || 'Unknown';
                          return ageCat.length > 12 ? ageCat.substring(0, 12) + '...' : ageCat;
                        })()}
                      </span>
                    </td>
                    {isPlayerStats && (
                      (() => {
                        const sportLower = getSport().toLowerCase();
                        return sportLower === 'tennis' || sportLower === 'badminton' || sportLower === 'table tennis' || sportLower === 'tt';
                      })() ? (
                        <>
                          <td>
                            {(() => { const s:any = extractStats(player); return (
                            <span style={{ 
                              fontWeight: '600', 
                              fontSize: '14px',
                              color: '#28a745'
                            }}>
                              {s.aces ?? 0}
                            </span>
                            ); })()}
                          </td>
                          <td>
                            {(() => { const s:any = extractStats(player); return (
                            <span style={{ 
                              fontWeight: '600', 
                              fontSize: '14px',
                              color: '#17a2b8'
                            }}>
                              {s.serviceFaults ?? 0}
                            </span>
                            ); })()}
                          </td>
                          <td>
                            {(() => { const s:any = extractStats(player); return (
                            <span style={{ 
                              fontWeight: '600', 
                              fontSize: '14px',
                              color: '#6f42c1'
                            }}>
                              {s.winners ?? 0}
                            </span>
                            ); })()}
                          </td>
                          <td>
                            {(() => { const s:any = extractStats(player); return (
                            <span style={{ 
                              fontWeight: '600', 
                              fontSize: '14px',
                              color: '#fd7e14'
                            }}>
                              {s.unforcedErrors ?? 0}
                            </span>
                            ); })()}
                          </td>
                          <td>
                            {(() => { const s:any = extractStats(player); return (
                            <span style={{ 
                              fontWeight: '600', 
                              fontSize: '14px',
                              color: '#dc3545'
                            }}>
                              {s.setsWon ?? 0}
                            </span>
                            ); })()}
                          </td>
                        </>
                      ) : (
                        <>
                          <td>
                            {(() => { const s:any = extractStats(player); return (
                            <span style={{ 
                              fontWeight: '600', 
                              fontSize: '14px',
                              color: '#28a745'
                            }}>
                              {s.goals ?? 0}
                            </span>
                            ); })()}
                          </td>
                          <td>
                            {(() => { const s:any = extractStats(player); return (
                            <span style={{ 
                              fontWeight: '600', 
                              fontSize: '14px',
                              color: '#17a2b8'
                            }}>
                              {s.assists ?? 0}
                            </span>
                            ); })()}
                          </td>
                          <td>
                            {(() => { const s:any = extractStats(player); return (
                            <span style={{ 
                              fontWeight: '600', 
                              fontSize: '14px',
                              color: '#6f42c1'
                            }}>
                              {s.passingAccuracy != null ? `${Number(s.passingAccuracy).toFixed(1)}%` : '0.0%'}
                            </span>
                            ); })()}
                          </td>
                          <td>
                            {(() => { const s:any = extractStats(player); return (
                            <span style={{ 
                              fontWeight: '600', 
                              fontSize: '14px',
                              color: '#fd7e14'
                            }}>
                              {s.shotsOnTarget ?? 0}
                            </span>
                            ); })()}
                          </td>
                          <td>
                            {(() => { const s:any = extractStats(player); return (
                            <span style={{ 
                              fontWeight: '600', 
                              fontSize: '14px',
                              color: '#dc3545'
                            }}>
                              {s.tackles ?? 0}
                            </span>
                            ); })()}
                          </td>
                        </>
                      )
                    )}
                    <td style={{ position: 'sticky', right: 0, background: 'white', zIndex: 10 }}>
                      <div className="tournament-actions">
                        <button
                          className="action-btn btn-view"
                          onClick={() => handleView(idx)}
                          title="View Player"
                        >
                          <i className="ri-eye-line"></i>
                        </button>
                        <button
                          className="action-btn btn-edit"
                          onClick={() => handleEdit(idx)}
                          title="Edit Player"
                        >
                          <i className="ri-pencil-line"></i>
                        </button>
                        {!isPlayerStats && (
                          <button
                            className="action-btn btn-delete"
                            onClick={() => handleDelete(idx)}
                            title="Delete Player"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
        
        {/* Pagination Controls - Match other tabs */}
        {totalPages > 1 && (
          <div className="pagination-section mt-4" style={{ 
            padding: '16px 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {/* Page Info */}
            <div className="page-info mb-3">
              <span className="text-muted" style={{ 
                fontSize: '14px', 
                fontWeight: '500',
                color: '#6c757d'
              }}>
                Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount} {isPlayerStats ? 'player stats' : 'players'}
              </span>
            </div>

            {/* Pagination Controls */}
            <div className="pagination-controls d-flex align-items-center justify-content-center">
              <div className="page-numbers d-flex align-items-center">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => onPageChange(page - 1)}
                  disabled={page === 1}
                  style={{ marginRight: '8px' }}
                >
                  Previous
                </Button>
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else {
                    const startPage = Math.max(1, Math.min(page - 2, totalPages - 4));
                    pageNum = startPage + i;
                  }
                  
                  return (
                    <Button
                      key={pageNum}
                      variant={pageNum === page ? "primary" : "outline-secondary"}
                      size="sm"
                      onClick={() => onPageChange(pageNum)}
                      style={{ marginRight: '4px', minWidth: '32px' }}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => onPageChange(page + 1)}
                  disabled={page === totalPages}
                  style={{ marginLeft: '8px' }}
                >
                  Next
                </Button>
              </div>
              
              <div className="page-size-selector ms-4 d-flex align-items-center">
                <Form.Select
                  size="sm"
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  style={{ 
                    minWidth: '100px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {PAGE_SIZE_OPTIONS.map(size => (
                    <option key={size} value={size}>{size} per page</option>
                  ))}
                </Form.Select>
              </div>
            </div>
          </div>
        )}
      <DeleteModal
        show={showDeleteModal}
        handleClose={closeDeleteModal}
        deleteModalFunction={confirmDelete}
        title="Delete Player"
        message={`Are you sure you want to delete '${deleteIndex !== null && Array.isArray(players) ? players[deleteIndex]?.name : ''}'?`}
      />

      {/* Bulk Upload Modal - Only for Player Stats */}
      {isPlayerStats && (
        <Modal 
          show={showBulkUploadModal} 
          onHide={showResponseData ? undefined : handleBulkUploadClose} 
          size="xl" 
          centered
          backdrop={showResponseData ? 'static' : true}
          keyboard={!showResponseData}
        >
          <Modal.Header closeButton={!showResponseData}>
            <Modal.Title>Bulk Upload Player Stats</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {uploadSuccess ? (
              <div className="text-center py-4">
                <div className="text-success mb-3">
                  <i className="ri-checkbox-circle-fill" style={{ fontSize: '3rem' }}></i>
                </div>
                <h5 className="text-success">Upload Successful!</h5>
                <p className="text-muted">Your player stats have been uploaded successfully. The page will refresh automatically.</p>
              </div>
            ) : showResponseData ? (
              <div>
                <div className="text-center mb-4">
                  <div className="text-warning mb-3">
                    <i className="ri-error-warning-line" style={{ fontSize: '3rem' }}></i>
                  </div>
                  <h5 className="text-warning">Please Reupload the Sheet by Resolving the Below Issues</h5>
                  <p className="text-muted">Review the errors and unmatched data below, fix them in your Excel file, and upload again.</p>
                </div>

                {/* Response Data Display */}
                <div className="border rounded p-3 mb-3" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  <h6 className="mb-3">Upload Results:</h6>
                  
                  {/* Handle the specific API response structure: data.errors, data.unmatchedPlayers, data.unmatchedMatches, data.unmatchedTeams */}
                  {(() => {
                    const data = uploadResponseData?.data || {};
                    const errors = data?.errors || [];
                    const unmatchedPlayers = data?.unmatchedPlayers || [];
                    const unmatchedMatches = data?.unmatchedMatches || [];
                    const unmatchedTeams = data?.unmatchedTeams || [];
                    
                    return (
                      <>
                        {/* Errors Table */}
                        {errors.length > 0 && (
                          <div className="mb-4">
                            <h6 className="text-danger mb-3">Errors ({errors.length}):</h6>
                            <div className="manage-tournaments-table-container">
                              <Table className="mb-0 manage-tournaments-table">
                                <thead>
                                  <tr>
                                    <th style={{ textAlign: 'left', padding: '12px 8px' }}>Error Details</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {errors.map((error: string, index: number) => (
                                    <tr key={index}>
                                      <td style={{ textAlign: 'left', padding: '12px 8px' }}>
                                        <div className="d-flex align-items-start">
                                          <i className="ri-error-warning-line text-danger me-2 mt-1"></i>
                                          <span>{error}</span>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        )}

                        {/* Unmatched Teams */}
                        {unmatchedTeams.length > 0 && (
                          <div className="mb-4">
                            <h6 className="text-warning mb-3">Unmatched Teams ({unmatchedTeams.length}):</h6>
                            <div className="manage-tournaments-table-container">
                              <Table className="mb-0 manage-tournaments-table">
                                <thead>
                                  <tr>
                                    <th style={{ textAlign: 'left', padding: '12px 8px' }}>Team Name</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {unmatchedTeams.map((team: string, index: number) => (
                                    <tr key={index}>
                                      <td style={{ textAlign: 'left', padding: '12px 8px' }}>
                                        <i className="ri-team-line text-warning me-2"></i>
                                        {team}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        )}

                        {/* Unmatched Players */}
                        {unmatchedPlayers.length > 0 && (
                          <div className="mb-4">
                            <h6 className="text-warning mb-3">Unmatched Players ({unmatchedPlayers.length}):</h6>
                            <div className="manage-tournaments-table-container">
                              <Table className="mb-0 manage-tournaments-table">
                                <thead>
                                  <tr>
                                    <th style={{ textAlign: 'left', padding: '12px 8px' }}>Player Name</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {unmatchedPlayers.map((player: string, index: number) => (
                                    <tr key={index}>
                                      <td style={{ textAlign: 'left', padding: '12px 8px' }}>
                                        <i className="ri-user-line text-warning me-2"></i>
                                        {player}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        )}

                        {/* Unmatched Matches */}
                        {unmatchedMatches.length > 0 && (
                          <div className="mb-4">
                            <h6 className="text-warning mb-3">Unmatched Matches ({unmatchedMatches.length}):</h6>
                            <div className="manage-tournaments-table-container">
                              <Table className="mb-0 manage-tournaments-table">
                                <thead>
                                  <tr>
                                    <th style={{ textAlign: 'left', padding: '12px 8px' }}>Match Number</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {unmatchedMatches.map((match: string | number, index: number) => (
                                    <tr key={index}>
                                      <td style={{ textAlign: 'left', padding: '12px 8px' }}>
                                        <i className="ri-calendar-event-line text-warning me-2"></i>
                                        {match}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </div>
                        )}

                        {/* Fallback if no data structure matches */}
                        {errors.length === 0 && unmatchedTeams.length === 0 && unmatchedPlayers.length === 0 && unmatchedMatches.length === 0 && (
                          <div className="alert alert-info">
                            <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}>
                              {JSON.stringify(uploadResponseData, null, 2)}
                            </pre>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                <div className="text-center">
                  <p className="text-muted">
                    <i className="ri-information-line me-1"></i>
                    You can upload a new file to fix the issues, or acknowledge the results to close this dialog.
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <div className="mb-3">
                  <p className="text-muted">Upload an Excel file (.xlsx or .xls) to bulk add player stats to this tournament.</p>
                </div>

                {/* Download Template */}
                <div className="mb-4">
                  <div className="d-flex align-items-center gap-3">
                    <h6 className="mb-0">
                      <i className="ri-file-info-line me-2"></i>
                      Excel Template
                    </h6>
                    <a
                      href={(() => {
                        const currentSport = getSport().toLowerCase();
                        const isRacketSport = currentSport === 'tennis' || currentSport === 'badminton' || currentSport === 'table tennis' || currentSport === 'tt';
                        // Racket sports template (Tennis, Badminton, Table Tennis)
                        if (isRacketSport) {
                          return "https://docs.google.com/spreadsheets/d/1CbqB9rlB5aCQ3VsifVydzC5GqDOUxVFY/edit?usp=sharing&ouid=110998283468080236376&rtpof=true&sd=true";
                        }
                        // Football and other sports template
                        return "https://docs.google.com/spreadsheets/d/17T0emqhzX1fsWiOmmaPclLsr_P9u1rF47XJ8REij0b8/edit?gid=0#gid=0";
                      })()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-outline-primary"
                      style={{ textDecoration: 'none' }}
                    >
                      <i className="ri-download-line me-1"></i>
                      Download Template
                    </a>
                  </div>
                  <small className="text-muted d-block mt-2">
                    <i className="ri-information-line me-1"></i>
                    Download the template, fill in your data, and upload the completed Excel file. Ensure Team Name, Player Name, and Age Category match existing records in the system.
                  </small>
                </div>

                {/* File Upload Area */}
                <div
                  className={`border-2 border-dashed rounded p-4 text-center ${
                    uploadFile ? 'border-success bg-light' : 'border-secondary'
                  }`}
                  onDrop={handleFileDrop}
                  onDragOver={handleDragOver}
                  style={{ minHeight: '200px', cursor: 'pointer' }}
                  onClick={() => document.getElementById('bulkUploadFileInput')?.click()}
                >
                  <input
                    id="bulkUploadFileInput"
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  
                  {uploadFile ? (
                    <div>
                      <div className="text-success mb-2">
                        <i className="ri-file-excel-2-fill" style={{ fontSize: '2rem' }}></i>
                      </div>
                      <h6 className="text-success">{uploadFile.name}</h6>
                      <p className="text-muted mb-0">File selected successfully</p>
                    </div>
                  ) : (
                    <div>
                      <div className="text-muted mb-3">
                        <i className="ri-upload-cloud-2-line" style={{ fontSize: '3rem' }}></i>
                      </div>
                      <h5>Drop your Excel file here</h5>
                      <p className="text-muted">or click to browse files</p>
                      <small className="text-muted">Supports .xlsx and .xls files</small>
                    </div>
                  )}
                </div>

                {/* Upload Progress */}
                {uploading && (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span>Uploading...</span>
                      <span>{uploadProgress}%</span>
                    </div>
                    <div className="progress">
                      <div
                        className="progress-bar progress-bar-striped progress-bar-animated"
                        role="progressbar"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Error Message */}
                {uploadError && (
                  <div className="alert alert-danger mt-3">
                    <div className="d-flex align-items-start">
                      <i className="ri-error-warning-line me-2 mt-1"></i>
                      <div>
                        <strong>Upload Failed</strong>
                        <div className="mt-1">{uploadError}</div>
                        <small className="text-muted d-block mt-2">
                          {uploadError.includes('File processing error') ? (
                            <>
                              <strong>Possible solutions:</strong><br/>
                              • Ensure your Excel file is not corrupted<br/>
                              • Try saving the file in a different Excel format (.xlsx)<br/>
                              • Check that the file contains valid data in all required columns<br/>
                              • Make sure the file is not password protected
                            </>
                          ) : (
                            'Please check your file format and try again. If the problem persists, contact support.'
                          )}
                        </small>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            {showResponseData ? (
              <>
                <Button variant="outline-secondary" onClick={handleReupload}>
                  <i className="ri-upload-line me-1"></i>
                  Upload New File
                </Button>
                <Button variant="primary" onClick={handleAcknowledgeResponse}>
                  <i className="ri-check-line me-1"></i>
                  Acknowledge & Close
                </Button>
              </>
            ) : !uploadSuccess && (
              <>
                <Button variant="secondary" onClick={handleBulkUploadClose} disabled={uploading}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleBulkUpload}
                  disabled={!uploadFile || uploading}
                >
                  {uploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Uploading...
                    </>
                  ) : (
                    'Upload Player Stats'
                  )}
                </Button>
              </>
            )}
          </Modal.Footer>
        </Modal>
      )}
      <TournamentBulkUploadModal
        show={showTournamentBulkUpload}
        onHide={() => setShowTournamentBulkUpload(false)}
        onSuccess={getBulkUploadSuccessHandler(onAdd, onRefresh)}
        resource={isPlayerStats ? "player-stats" : "players"}
        tournamentId={tournamentId}
        tournamentSeasonId={tournamentSeasonId}
        sport={tournamentSports}
      />
    </div>
  );
};

export default PlayersTable;